import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2, Trash2, Bug } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const ADMIN_EMAIL = "ahmed@supplyfied.com";

interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  messageId?: string; // server-issued chat_messages.id for debug lookup
}

export function InvestorChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [debugLog, setDebugLog] = useState<{ system_prompt: string; user_message: string; initial_reply: string; final_reply: string; adherence_passed: boolean; adherence_reason: string; was_corrected: boolean } | null>(null);
  const [debugLoading, setDebugLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Detect admin
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      setIsAdmin(data.user?.email === ADMIN_EMAIL);
    });
  }, []);

  const openDebug = async (messageId: string) => {
    setDebugLoading(true);
    setDebugLog(null);
    const { data, error } = await supabase
      .from("ai_assistant_debug_logs")
      .select("system_prompt, user_message, initial_reply, final_reply, adherence_passed, adherence_reason, was_corrected")
      .eq("assistant_message_id", messageId)
      .maybeSingle();
    setDebugLoading(false);
    if (error || !data) { toast.error("Debug log not found"); return; }
    setDebugLog(data as typeof debugLog);
  };

  // Check assistant kill switch
  useEffect(() => {
    supabase
      .from("ai_assistant_config")
      .select("enabled")
      .limit(1)
      .maybeSingle()
      .then(({ data }) => {
        if (data && data.enabled === false) setEnabled(false);
      });
  }, []);


  // Load history when opened first time
  useEffect(() => {
    if (!open || messages.length > 0) return;
    const load = async () => {
      setLoadingHistory(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { setLoadingHistory(false); return; }
      const { data } = await supabase
        .from("chat_messages")
        .select("id, role, content")
        .eq("user_id", user.id)
        .order("created_at", { ascending: true })
        .limit(100);
      if (data) {
        setMessages(data.filter(m => m.role !== "system").map(m => ({
          id: m.id,
          role: m.role as "user" | "assistant",
          content: m.content,
        })));
      }
      setLoadingHistory(false);
    };
    load();
  }, [open, messages.length]);

  // Autoscroll
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  // Focus input
  useEffect(() => {
    if (open && !sending) inputRef.current?.focus();
  }, [open, sending, messages.length]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    const optimistic: ChatMessage = { id: crypto.randomUUID(), role: "user", content: text };
    setMessages(m => [...m, optimistic]);
    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke("investor-chat", {
        body: { message: text },
      });
      if (error) throw error;
      const reply = (data as { reply?: string; error?: string })?.reply ?? "";
      const messageId = (data as { message_id?: string })?.message_id;
      if (!reply) throw new Error((data as { error?: string })?.error ?? "No reply");
      setMessages(m => [...m, { id: crypto.randomUUID(), role: "assistant", content: reply, messageId }]);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Failed to get a reply";
      toast.error(msg);
      setMessages(m => [...m, {
        id: crypto.randomUUID(),
        role: "assistant",
        content: "Sorry, I couldn't process that just now. Please try again.",
      }]);
    } finally {
      setSending(false);
    }
  };

  const clearHistory = async () => {
    if (!confirm("Clear this conversation?")) return;
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { error } = await supabase.from("chat_messages").delete().eq("user_id", user.id);
    if (error) { toast.error("Failed to clear"); return; }
    setMessages([]);
    toast.success("Conversation cleared");
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  if (!enabled) return null;

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label="Open investor assistant"
          className="fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-lg hover:scale-105 transition-transform flex items-center justify-center"
        >
          <MessageCircle className="h-6 w-6" />
        </button>
      )}

      {/* Panel */}
      {open && (
        <div className="fixed bottom-6 right-6 z-50 w-[min(420px,calc(100vw-2rem))] h-[min(620px,calc(100vh-3rem))] rounded-xl border bg-card text-card-foreground shadow-2xl flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/50">
            <div>
              <div className="font-semibold text-sm">Investor Assistant</div>
              <div className="text-xs text-muted-foreground">Ask anything about Supplyfied</div>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={clearHistory} title="Clear conversation">
                <Trash2 className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setOpen(false)} aria-label="Close">
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
            {loadingHistory && (
              <div className="flex justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            )}
            {!loadingHistory && messages.length === 0 && (
              <div className="text-sm text-muted-foreground space-y-3">
                <p>Hi! I'm your investor assistant. I can answer questions grounded in the Investor Hub data — business, financials, traction, cap table, legal, and more. I can also benchmark, sanity-check claims, and run computations.</p>
                <p className="font-medium text-foreground">Try asking:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>What is the current burn rate and runway?</li>
                  <li>How does our traction compare to similar startups?</li>
                  <li>Do the financial projections look realistic?</li>
                  <li>Walk me through the cap table.</li>
                </ul>
              </div>
            )}
            {messages.map(m => (
              <div key={m.id} className={cn("flex", m.role === "user" ? "justify-end" : "justify-start")}>
                {m.role === "user" ? (
                  <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-primary text-primary-foreground px-3 py-2 text-sm whitespace-pre-wrap">
                    {m.content}
                  </div>
                ) : (
                  <div className="max-w-[90%] flex flex-col gap-1">
                    <div className="text-sm prose prose-sm dark:prose-invert max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
                    </div>
                    {isAdmin && m.messageId && (
                      <button
                        onClick={() => openDebug(m.messageId!)}
                        className="self-start inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground hover:text-foreground"
                      >
                        <Bug className="h-3 w-3" /> Show prompt
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Thinking…
                </div>
              </div>
            )}
          </div>

          <div className="border-t p-3">
            <div className="flex items-end gap-2">
              <Textarea
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                rows={1}
                placeholder="Ask about financials, traction, cap table…"
                className="min-h-[40px] max-h-32 resize-none"
                disabled={sending}
              />
              <Button onClick={send} disabled={!input.trim() || sending} size="icon" className="shrink-0">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={!!debugLog || debugLoading} onOpenChange={(o) => { if (!o) setDebugLog(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Assistant debug — assembled prompt</DialogTitle>
          </DialogHeader>
          {debugLoading && <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>}
          {debugLog && (
            <div className="space-y-3 overflow-auto text-sm">
              <div className="flex gap-2 text-xs">
                <span className={cn("px-2 py-0.5 rounded", debugLog.adherence_passed ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800")}>
                  Adherence: {debugLog.adherence_passed ? "passed" : "violation"}
                </span>
                {debugLog.was_corrected && <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-800">Auto-corrected</span>}
              </div>
              {debugLog.adherence_reason && <p className="text-xs text-muted-foreground">Reason: {debugLog.adherence_reason}</p>}
              <div>
                <div className="text-xs uppercase text-muted-foreground mb-1">User question</div>
                <div className="rounded border bg-muted/30 p-2 whitespace-pre-wrap">{debugLog.user_message}</div>
              </div>
              {debugLog.was_corrected && (
                <details>
                  <summary className="cursor-pointer text-xs uppercase text-muted-foreground">Initial (pre-correction) reply</summary>
                  <div className="mt-1 rounded border bg-muted/20 p-2 whitespace-pre-wrap">{debugLog.initial_reply}</div>
                </details>
              )}
              <div>
                <div className="text-xs uppercase text-muted-foreground mb-1">Assembled system prompt</div>
                <pre className="max-h-[50vh] overflow-auto rounded border bg-muted/30 p-2 text-xs whitespace-pre-wrap font-mono">{debugLog.system_prompt}</pre>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
